import { Button, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Stack, Text, useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { adminUpdateCompanyBizRegNo } from "../../../api";
import { colors } from "../../../styles";

interface EditBizRegNoModalProps {
    isOpen: boolean;
    onClose: () => void;
    companyId: string;
    companyName: string;
    bizRegNo?: string;
}

interface IEditBizRegNoForm {
    bizRegNo: string;
}

export default function EditBizRegNoModal(props: EditBizRegNoModalProps) {

    const queryClient = useQueryClient();
    const toast = useToast();

    const { register, handleSubmit, reset, watch, formState: { isValid } } = useForm<IEditBizRegNoForm>({
        mode: "onChange",
        defaultValues: {
            bizRegNo: props.bizRegNo ?? "",
        },
    });

    useEffect(() => {
        if (props.isOpen) {
            reset({ bizRegNo: props.bizRegNo ?? "" });
        }
    }, [props.isOpen, props.bizRegNo, reset]);

    const currentValue = watch("bizRegNo") ?? "";
    const originalValue = props.bizRegNo ?? "";
    const isChanged = currentValue.trim() !== originalValue.trim();

    const updateBizRegNoMutation = useMutation({
        mutationFn: adminUpdateCompanyBizRegNo,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetCompanies'] });
            props.onClose();
            toast({
                title: "사업자 등록번호를 저장했습니다.",
                status: "success",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
        onError: (error) => {
            console.error(error);
            toast({
                title: "사업자 등록번호 저장에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const onSubmit = (data: IEditBizRegNoForm) => {
        updateBizRegNoMutation.mutate({
            companyId: props.companyId,
            bizRegNo: data.bizRegNo.trim(),
        });
    };

    return (
        <Modal isOpen={props.isOpen} onClose={props.onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text fontSize={20} fontWeight={700} color={colors.black}>
                        {props.companyName || "-"}
                    </Text>
                    <ModalCloseButton />
                </ModalHeader>
                <ModalBody>
                    <Stack gap={3}>
                        <Text fontSize={14} fontWeight={400} color={colors.gray}>
                            사업자 등록번호
                        </Text>
                        <Input
                            {...register("bizRegNo", { required: true, minLength: 1 })}
                            type="text"
                            placeholder="사업자 등록번호를 입력해주세요."
                        />
                    </Stack>
                </ModalBody>
                <Stack marginTop={5} width={"100%"} padding={3}>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        isLoading={updateBizRegNoMutation.isPending}
                        isDisabled={!isValid || !isChanged}
                        width={"100%"}
                        backgroundColor={colors.blue}
                        color={colors.white}
                        borderRadius={10}
                        _hover={{ bg: colors.blue }}
                        boxShadow="sm"
                    >
                        {originalValue ? "수정" : "등록"}
                    </Button>
                </Stack>
            </ModalContent>
        </Modal>
    );
}
